<template>
  <div :class="ns.b()">
    <div v-if="loading" :class="ns.e('loading')">{{ t('z.rules.loading') }}</div>
    <div v-else-if="error" :class="ns.e('error')">{{ error.message }}</div>
    <div v-else-if="!fieldList.length" :class="ns.e('empty')">
      {{ t('z.rules.empty') }}
    </div>
    <template v-else>
      <rules-summary
        v-if="showSummary"
        :rule-groups="ruleGroups"
        :field-list="fieldList"
      />
      <div :class="ns.e('panel')">
        <div
          v-for="(group, groupIndex) in ruleGroups"
          :key="group.id"
          class="filter-group"
        >
          <div class="filter-group__header">
            <span class="filter-group__label">
              <template v-if="groupIndex === 0">{{ t('z.table.filterGroup') }}</template>
              <template v-else>
                <z-select
                  v-model="group.groupLogic"
                  :disabled="disabled"
                  class="filter-group__group-logic"
                >
                  <z-option
                    v-for="opt in LOGIC_OPTIONS"
                    :key="opt.value"
                    :value="opt.value"
                    :label="t(opt.label)"
                  />
                </z-select>
                <span class="filter-group__label-text">{{ t('z.table.filterGroup') }}</span>
              </template>
            </span>
            <div class="filter-group__header-actions">
              <z-select
                v-model="group.logic"
                :disabled="disabled"
                size="small"
                class="filter-group__inner-logic"
              >
                <z-option value="and" :label="t('z.table.filterGroupInnerAnd')" />
                <z-option value="or" :label="t('z.table.filterGroupInnerOr')" />
              </z-select>
              <z-button
                v-if="ruleGroups.length > 1 && !disabled"
                link
                type="danger"
                @click="removeGroup(groupIndex)"
              >
                {{ t('z.table.removeFilterGroup') }}
              </z-button>
            </div>
          </div>

          <div
            v-for="(cond, condIndex) in group.conditions"
            :key="cond.id"
            class="filter-row"
          >
            <div class="filter-row__remove">
              <z-button
                v-if="group.conditions.length > 1 && !disabled"
                link
                type="danger"
                @click="removeCondition(groupIndex, condIndex)"
              >
                x
              </z-button>
            </div>
            <div class="filter-row__logic">
              <z-select
                v-if="condIndex !== 0"
                v-model="cond.logic"
                :disabled="disabled"
                class="filter-row__logic-select"
              >
                <z-option
                  v-for="opt in LOGIC_OPTIONS"
                  :key="opt.value"
                  :value="opt.value"
                  :label="t(opt.label)"
                />
              </z-select>
            </div>
            <z-select
              v-model="cond.field"
              :disabled="disabled"
              class="filter-row__field"
              @change="fieldChange(cond)"
            >
              <z-option
                v-for="item in fieldList"
                :key="item.field"
                :value="item.field"
                :label="item.name"
              />
            </z-select>
            <z-select
              v-model="cond.operator"
              :disabled="disabled"
              class="filter-row__operator"
            >
              <z-option
                v-for="opt in cond.operatorList"
                :key="opt.value"
                :value="opt.value"
                :label="t(opt.label)"
              />
            </z-select>
            <rule-value
              v-if="!isValuelessOperator(cond.operator)"
              v-model="cond.value"
              v-model:value-label="cond.valueLabel"
              :type="cond.type"
              :data-source="cond.dataSource"
              :field-key="cond.field"
              :value-label="cond.valueLabel"
              :disabled="disabled"
              class="filter-row__value"
            />
            <div v-else class="filter-row__value filter-row__value--empty" />
          </div>

          <z-button
            v-if="!disabled"
            class="filter-group__add-cond"
            link
            @click="addCondition(groupIndex)"
          >
            {{ t('z.table.addFilter') }}
          </z-button>
        </div>

        <div :class="ns.e('footer')">
          <z-button v-if="!disabled" @click="clearAll">{{ t('z.table.clearFilter') }}</z-button>
          <div>
            <z-button v-if="!disabled" @click="addGroup">{{ t('z.table.addFilterGroup') }}</z-button>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, toRef, watch } from 'vue'
import ZSelect, { ZOption } from '@zoryn/components/select'
import ZButton from '@zoryn/components/button'
import { useLocale, useNamespace } from '@zoryn/hooks'
import RuleValue from './rule-value.vue'
import RulesSummary from './rules-summary.vue'
import {
  buildRuleDisplayGroups,
  formatRuleDisplayText,
} from './rule-display'
import { LOGIC_OPTIONS, getRuleOperator, resolveRuleColumnType } from './rule-meta'
import {
  createRuleCondition,
  createRuleGroup,
  ensureDefaultRuleGroups,
} from './rule-group'
import {
  buildRulePayload,
  getFirstRuleField,
  isSameRulePayload,
  isValuelessOperator,
  parseRulePayload,
} from './rule-utils'
import { useRulesParams } from './use-rules-params'
import { rulesConditionsEmits, rulesConditionsProps } from './rules-conditions'

import type { FilterPayloadNode } from '@zoryn/components/grid'
import type { RuleCondition, RuleGroupState } from './rules'

defineOptions({
  name: 'ZRulesConditions',
})

const props = defineProps(rulesConditionsProps)
const emit = defineEmits(rulesConditionsEmits)

const ns = useNamespace('rules-conditions')
const { t } = useLocale()

const ruleGroups = ref<RuleGroupState[]>([])
let syncingFromProps = false

const { fieldList, loading, error, reload } = useRulesParams({
  system: toRef(props, 'system'),
  module: toRef(props, 'module'),
})

const payload = computed(() => buildRulePayload(ruleGroups.value))

function runSyncFromProps(fn: () => void) {
  syncingFromProps = true
  try {
    fn()
  } finally {
    syncingFromProps = false
  }
}

function applyPayload(value: FilterPayloadNode | FilterPayloadNode[] | null) {
  if (!fieldList.value.length) {
    ruleGroups.value = []
    return
  }
  const parsed = parseRulePayload(value, fieldList.value)
  if (!parsed.length) {
    ruleGroups.value = []
    ensureDefaultGroups()
    return
  }
  ruleGroups.value = parsed
}

function resolveDefaultField() {
  return getFirstRuleField(fieldList.value)
}

function ensureDefaultGroups() {
  const field = resolveDefaultField()
  ensureDefaultRuleGroups(ruleGroups.value, field)
}

watch(fieldList, () => {
  if (!fieldList.value.length) {
    ruleGroups.value = []
    return
  }
  if (props.modelValue != null) {
    if (isSameRulePayload(props.modelValue, payload.value)) {
      return
    }
    runSyncFromProps(() => applyPayload(props.modelValue))
    return
  }
  if (!ruleGroups.value.length) {
    runSyncFromProps(() => ensureDefaultGroups())
  }
})

watch(
  () => props.modelValue,
  (value) => {
    if (isSameRulePayload(value ?? null, payload.value)) {
      return
    }
    runSyncFromProps(() => applyPayload(value ?? null))
  },
  { deep: true }
)

watch(
  ruleGroups,
  () => {
    if (syncingFromProps) {
      return
    }
    const next = payload.value
    if (isSameRulePayload(next, props.modelValue ?? null)) {
      return
    }
    emit('update:modelValue', next)
    emit('change', next)
  },
  { deep: true }
)

function removeGroup(groupIndex: number) {
  ruleGroups.value.splice(groupIndex, 1)
}

function removeCondition(groupIndex: number, condIndex: number) {
  const group = ruleGroups.value[groupIndex]
  group.conditions.splice(condIndex, 1)
  if (!group.conditions.length) {
    removeGroup(groupIndex)
  }
}

function clearAll() {
  ruleGroups.value = []
  ensureDefaultGroups()
}

function addCondition(groupIndex: number) {
  const field = resolveDefaultField()
  if (!field) return
  ruleGroups.value[groupIndex].conditions.push(createRuleCondition(field))
}

function addGroup() {
  const field = resolveDefaultField()
  if (!field) return
  ruleGroups.value.push(createRuleGroup(field))
}

function fieldChange(cond: RuleCondition) {
  const param = fieldList.value.find((item) => item.field === cond.field)
  if (!param) return
  cond.type = resolveRuleColumnType(param.columnType)
  cond.dataSource = param.dataSource
  cond.operatorList = getRuleOperator(cond.type)
  cond.operator =
    cond.type === 'number' || cond.type === 'date'
      ? 'eq'
      : cond.type === 'switch'
        ? 'eq'
        : 'like'
  cond.value = cond.type === 'switch' ? false : null
  cond.valueLabel = undefined
}

function getDescription() {
  const display = buildRuleDisplayGroups(ruleGroups.value, fieldList.value, t)
  return formatRuleDisplayText(display, t)
}

defineExpose({
  getPayload: () => payload.value,
  getDescription,
  reload,
})
</script>
